import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';

import { Compbaser } from '../compbaser';
import { SceneCardTemplateService, ISceneTemplate, ITemplateResolution, ITemplateCategory } from '../../services/scene-card-template.service';
import { CardSceneCreatorService } from '../../services/card-scene-creator.service';
import { ISceneBlock } from '../../interfaces';

export interface DialogSceneCardBrowserData {
    selectedTemplate?: ISceneTemplate;
}

export interface DialogSceneCardBrowserResult {
    template: ISceneTemplate;
    scene: { Player: ISceneBlock };
}

interface ICategoryChip {
    key: string;
    name: string;
    icon: string;
    count: number;
}

@Component({
    selector: 'app-dialog-scene-card-browser',
    templateUrl: './dialog-scene-card-browser.component.html',
    styleUrls: ['./dialog-scene-card-browser.component.scss'],
    standalone: false
})
export class DialogSceneCardBrowserComponent extends Compbaser implements OnInit {
    @ViewChild('scrollContainer') scrollContainer: ElementRef;

    selectedTemplate: ISceneTemplate | null = null;
    filteredTemplates: ISceneTemplate[] = [];
    visibleTemplates: ISceneTemplate[] = [];
    allTemplates: ISceneTemplate[] = [];
    searchQuery = '';
    loading = false;

    categories: ICategoryChip[] = [];
    activeCategory = '';
    activeOrientation = '';
    batchSize = 60;
    canLoadMore = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: DialogSceneCardBrowserData,
        private dialogRef: MatDialogRef<DialogSceneCardBrowserComponent>,
        public override dialog: MatDialog,
        private sceneCardTemplateService: SceneCardTemplateService,
        private cardSceneCreatorService: CardSceneCreatorService,
        private translocoService: TranslocoService
    ) {
        super(dialog);
        if (data?.selectedTemplate) {
            this.selectedTemplate = data.selectedTemplate;
        }
    }

    ngOnInit(): void {
        this.allTemplates = this.sceneCardTemplateService.getTemplates();

        // Build category chips with counts
        const grouped = this.sceneCardTemplateService.getTemplatesByCategories();
        this.categories = Object.keys(grouped).map(key => {
            const info = this.sceneCardTemplateService.getCategoryInfo(key);
            return {
                key,
                name: info?.name || key,
                icon: info?.icon || 'folder',
                count: grouped[key].length
            };
        });

        this.applyFilters();
    }

    selectTemplate(template: ISceneTemplate): void {
        this.selectedTemplate = template;
    }

    onSearchChange(): void {
        this.applyFilters();
    }

    clearSearch(): void {
        this.searchQuery = '';
        this.applyFilters();
    }

    toggleCategory(key: string): void {
        this.activeCategory = this.activeCategory === key ? '' : key;
        this.applyFilters();
    }

    setOrientation(orientation: string): void {
        this.activeOrientation = this.activeOrientation === orientation ? '' : orientation;
        this.applyFilters();
    }

    applyFilters(): void {
        // Build base results with orientation + search (excluding category)
        let baseResults = [...this.allTemplates];

        let matchingRes: string[] = [];
        if (this.activeOrientation) {
            const resNames = this.sceneCardTemplateService.getResolutionNames();
            matchingRes = resNames.filter(rKey => {
                const info = this.sceneCardTemplateService.getResolutionInfo(rKey);
                return info?.orientation === this.activeOrientation;
            });
            baseResults = baseResults.filter(t => matchingRes.includes(t.resolution));
        }

        if (this.searchQuery.trim()) {
            const q = this.searchQuery.toLowerCase();
            baseResults = baseResults.filter(t =>
                t.name.toLowerCase().includes(q) ||
                t.description.toLowerCase().includes(q) ||
                t.tags.some(tag => tag.toLowerCase().includes(q)) ||
                t.category.toLowerCase().includes(q)
            );
        }

        // Update chip counts based on base results (before category filter)
        for (const cat of this.categories) {
            cat.count = baseResults.filter(t => t.category === cat.key).length;
        }

        // Now apply category filter
        let results = baseResults;
        if (this.activeCategory) {
            results = results.filter(t => t.category === this.activeCategory);
        }

        this.filteredTemplates = results;
        this.visibleTemplates = results.slice(0, this.batchSize);
        this.canLoadMore = results.length > this.batchSize;
    }

    loadMore(): void {
        const next = this.visibleTemplates.length + this.batchSize;
        this.visibleTemplates = this.filteredTemplates.slice(0, next);
        this.canLoadMore = next < this.filteredTemplates.length;
    }

    onScroll(event: Event): void {
        if (!this.canLoadMore) return;
        const el = event.target as HTMLElement;
        const threshold = 200;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
            this.loadMore();
        }
    }

    async applyTemplate() {
        if (this.selectedTemplate) {
            this.loading = true;

            try {
                this.sceneCardTemplateService.setCurrentTemplate(this.selectedTemplate);

                // Create the actual scene from the template
                const scene = await this.cardSceneCreatorService.createSceneFromTemplate(this.selectedTemplate);

                // Return both template and scene
                this.dialogRef.close({
                    template: this.selectedTemplate,
                    scene: scene
                });
            } catch (error) {
                console.error('Error applying template:', error);
                this.loading = false;
            }
        }
    }

    cancel(): void {
        this.dialogRef.close();
    }

    getRandomTemplate(): void {
        this.selectedTemplate = this.sceneCardTemplateService.getRandomTemplate();
    }

    getCategoryInfo(category: string): ITemplateCategory | undefined {
        return this.sceneCardTemplateService.getCategoryInfo(category);
    }

    getResolutionInfo(resolution: string): ITemplateResolution | undefined {
        return this.sceneCardTemplateService.getResolutionInfo(resolution);
    }
}
